import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   * Applies the stored layout for the active viewport (defaults to card grid when unset).
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const isDesktop = event.matches;
    const storageKey = isDesktop ? 'product-grid-view-desktop' : 'product-grid-view-mobile';
    let stored = sessionStorage.getItem(storageKey);
    if (stored === null) {
      stored = 'default';
    }

    const { grid } = this.refs;
    if (grid) {
      grid.setAttribute('product-grid-view', stored);
    }

    const radioName = isDesktop ? 'grid' : 'grid-mobile';
    const options = this.querySelectorAll(`input[type="radio"][name="${radioName}"]`);
    for (const option of options) {
      if (option instanceof HTMLInputElement && option.value === stored) {
        option.checked = true;
        return;
      }
    }
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
